/** Lifecycle status from kk-admin-sa-governance employee APIs. */
export type EmployeeBackendStatus =
  | "candidate"
  | "pending"
  | "active"
  | "suspended"
  | "deleted";

/** Display values for the existing Active column (Option B). */
export type EmployeeActiveDisplay =
  | "Yes"
  | "Pending Employee"
  | "Awaiting Activation"
  | "Suspended"
  | "Deleted";

/** Wire shape from GET /sa/employees. */
export interface ApiEmployee {
  employeeId: string | null;
  auth0Sub: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  emailAddress: string | null;
  jobTitle: string | null;
  employeeRoleKey: string | null;
  roleDisplayName: string | null;
  status: string | null;
  createdAt: string | null;
  departmentId?: string | null;
  departmentDisplayName?: string | null;
  /** Resolved display label for list/detail views. */
  department?: string | null;
  manager?: string | null;
  hireDate?: string | null;
  updatedAt?: string | null;
  suspendedAt?: string | null;
  suspensionReason?: string | null;
  latestLifecycleReason?: string | null;
  latestLifecycleEventType?: string | null;
  latestLifecycleAt?: string | null;
  latestLifecycleBy?: string | null;
  latestLifecycleByName?: string | null;
  latestLifecycleByEmail?: string | null;
  notes?: string | null;
}

/** Wire shape from GET /sa/employees/{employeeId}. */
export interface ApiEmployeeDetail extends ApiEmployee {
  department: string | null;
  manager: string | null;
  hireDate: string | null;
  createdBy: string | null;
  updatedBy: string | null;
}

/** Wire shape from GET /sa/employees/candidates. */
export interface ApiCandidate {
  auth0Sub: string;
  emailAddress: string | null;
  displayName: string | null;
  status: "candidate";
}

/** View model for the existing Employees table (layout unchanged). */
export interface Employee {
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  role: string;
  active: boolean;
  activeDisplay: EmployeeActiveDisplay;
  hireDate: string;
  manager: string;
  city: string;
  state: string;
  region: string;
  auth0Sub: string;
  backendStatus: EmployeeBackendStatus;
  rowKey: string;
  latestLifecycleReason?: string | null;
  latestLifecycleEventType?: string | null;
  latestLifecycleAt?: string | null;
  latestLifecycleBy?: string | null;
  latestLifecycleByName?: string | null;
  latestLifecycleByEmail?: string | null;
  suspensionReason?: string | null;
}

export const EMPLOYEE_ID_PLACEHOLDER = "—";

export const SUPER_ADMIN_ROLE_KEY = "super-admin";

export type { ApiEmployeeRole } from "./employeeRoleTypes";

export interface AddEmployeeRequest {
  emailAddress: string;
  firstName: string;
  lastName: string;
  employeeRoleKey: string;
  jobTitle?: string;
  notes?: string;
  auth0Sub?: string;
  departmentId?: string | null;
  manager?: string;
  hireDate?: string;
}

/** @deprecated Use AddEmployeeRequest */
export type EnrollEmployeeRequest = AddEmployeeRequest;

export interface AddEmployeeResponse extends EmployeeMutationResponse {
  outcome?: string;
  passwordEmailSent?: boolean;
}

export interface EmployeeLifecycleReasonRequest {
  auth0Sub: string;
  reason: string;
}

export interface EmployeeMutationResponse {
  employee: ApiEmployee;
}

export interface HardDeleteEmployeeRequest {
  employeeId: string;
  confirmPhrase: string;
  confirmEmployeeId: string;
  reason: string;
}

export interface HardDeleteEmployeeResponse {
  employeeId: string;
  deleted: boolean;
}

export interface PurgeEmployeeRequest {
  employeeId: string;
  confirmPhrase: string;
  confirmEmployeeId: string;
  reason: string;
}

export interface PurgeEmployeeResponse {
  employeeId: string;
  purged: boolean;
}

/** Wire shape from GET /sa/employees/{employeeId}/audit. */
export interface ApiEmployeeAuditEvent {
  eventId: string | null;
  eventType: string | null;
  eventTimestamp: string | null;
  actorSub: string | null;
  actorName?: string | null;
  actorEmail?: string | null;
  targetSub: string | null;
  sourceIp?: string | null;
  userAgent?: string | null;
  status?: string | null;
  employeeId?: string | null;
  details: Record<string, unknown>;
}
